import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Select,
  Button,
  message,
  Spin,
  Typography,
  Divider,
  Space,
  InputNumber,
  Row,
  Col,
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import {
  fetchOverviewPreferences,
  updateOverviewPreferences,
  fetchAdmins,
  fetchDecisionStatuses,
  OverviewPreferences,
  StatusWithDecision,
} from "../services/applicationsOverviewPreferencesService";
import {
  StatusBadge,
  BadgeSeverity,
} from "./applicationsOverview/components/StatusBadge";
import { colors } from "../styles/theme";

const { Title, Text } = Typography;

interface ApplicationsOverviewSettingsModalProps {
  visible: boolean;
  onClose: () => void;
  organizationKey: string;
  onSave?: () => void; // Callback to refresh overview data
}

const ApplicationsOverviewSettingsModal: React.FC<ApplicationsOverviewSettingsModalProps> = ({
  visible,
  onClose,
  organizationKey,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<OverviewPreferences>({
    defaultInboxUserIds: [],
    statusCategorization: {},
    alertThresholds: {
      pendingApplications: { warning: 5, critical: 10 },
      unreadApplications: { warning: 5, critical: 10 },
      newActivity: { warning: 5, critical: 10 },
      unassigned: { warning: 5, critical: 10 },
    },
  });
  const [availableStatuses, setAvailableStatuses] = useState<
    StatusWithDecision[]
  >([]);
  const [admins, setAdmins] = useState<
    Array<{ id: number; firstName: string; lastName: string }>
  >([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [prefsResponse, adminList, statusesResponse] = await Promise.all([
        fetchOverviewPreferences(organizationKey),
        fetchAdmins(organizationKey),
        fetchDecisionStatuses(organizationKey),
      ]);

      // Set preferences with defaults to prevent crashes
      const loadedPrefs = prefsResponse.dashboardPreferences || {
        defaultInboxUserIds: [],
        statusCategorization: {},
      };

      // Merge alert thresholds with defaults
      const defaultThresholds = {
        pendingApplications: { warning: 5, critical: 10 },
        unreadApplications: { warning: 5, critical: 10 },
        newActivity: { warning: 5, critical: 10 },
        unassigned: { warning: 5, critical: 10 },
      };

      const savedThresholds = loadedPrefs.alertThresholds;

      setPreferences({
        ...loadedPrefs,
        alertThresholds: {
          pendingApplications: {
            warning:
              savedThresholds?.pendingApplications?.warning ??
              defaultThresholds.pendingApplications.warning,
            critical:
              savedThresholds?.pendingApplications?.critical ??
              defaultThresholds.pendingApplications.critical,
          },
          unreadApplications: {
            warning:
              savedThresholds?.unreadApplications?.warning ??
              defaultThresholds.unreadApplications.warning,
            critical:
              savedThresholds?.unreadApplications?.critical ??
              defaultThresholds.unreadApplications.critical,
          },
          newActivity: {
            warning:
              savedThresholds?.newActivity?.warning ??
              defaultThresholds.newActivity.warning,
            critical:
              savedThresholds?.newActivity?.critical ??
              defaultThresholds.newActivity.critical,
          },
          unassigned: {
            warning:
              savedThresholds?.unassigned?.warning ??
              defaultThresholds.unassigned.warning,
            critical:
              savedThresholds?.unassigned?.critical ??
              defaultThresholds.unassigned.critical,
          },
        },
      });

      // Sort statuses by decision, then title, then product title
      const sortedStatuses = (statusesResponse.records || []).sort((a, b) => {
        // Sort by decision first (null values last)
        if (a.decision !== b.decision) {
          if (!a.decision) return 1;
          if (!b.decision) return -1;
          return a.decision.localeCompare(b.decision);
        }

        // Then by title
        if (a.title !== b.title) {
          return a.title.localeCompare(b.title);
        }

        // Finally by product title
        const aProductTitle = a.financialInstitutionProduct?.title || "";
        const bProductTitle = b.financialInstitutionProduct?.title || "";
        return aProductTitle.localeCompare(bProductTitle);
      });

      setAvailableStatuses(sortedStatuses);

      // Sort admins alphabetically by first name, then last name
      const sortedAdmins = (adminList || []).sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setAdmins(sortedAdmins);
    } catch (error) {
      console.error("[ApplicationsOverviewSettingsModal] Error loading data:", error);
      message.error("Failed to load overview settings");
    } finally {
      setLoading(false);
    }
  }, [organizationKey]);

  useEffect(() => {
    if (visible) {
      loadData();
    }
  }, [visible, loadData]);

  const handleSave = async () => {
    // Validate alert thresholds before saving
    const thresholds = preferences.alertThresholds;
    const validationErrors: string[] = [];

    if (thresholds) {
      // Check each threshold type
      const thresholdTypes = [
        { key: "pendingApplications", label: "Pending Applications" },
        { key: "unreadApplications", label: "Unread Applications" },
        { key: "newActivity", label: "New Activity" },
        { key: "unassigned", label: "Unassigned Applications" },
      ] as const;

      thresholdTypes.forEach(({ key, label }) => {
        const threshold = thresholds[key];
        if (threshold && threshold.warning >= threshold.critical) {
          validationErrors.push(
            `${label}: Warning threshold (${threshold.warning}) must be less than Critical threshold (${threshold.critical})`
          );
        }
      });
    }

    // Show validation errors if any
    if (validationErrors.length > 0) {
      message.error({
        content: (
          <div>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>
              Please fix the following validation errors:
            </div>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        ),
        duration: 5,
      });
      return;
    }

    setSaving(true);
    try {
      await updateOverviewPreferences(organizationKey, preferences);
      message.success("Overview settings saved successfully");
      onClose();
      // Trigger overview data refresh if callback provided
      if (onSave) {
        onSave();
      }
    } catch (error) {
      message.error("Failed to save overview settings");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Overview Settings"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="cancel"
          onClick={onClose}
          style={{
            borderColor: colors.brandPrimary,
            color: colors.brandPrimary,
            backgroundColor: "transparent",
          }}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          loading={saving}
          onClick={handleSave}
          style={{
            backgroundColor: colors.brandPrimary,
            borderColor: colors.brandPrimary,
            color: colors.white,
          }}
        >
          Save
        </Button>,
      ]}
      width={600}
      bodyStyle={{
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
      }}
      style={{ top: 20 }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          <Title level={5}>Default Inbox Users</Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Select users that should be treated as "default inboxes" rather than
            individual admins. Applications assigned to these users will count
            as "unassigned" in overview metrics.
          </Text>

          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Select default inbox users"
            value={preferences.defaultInboxUserIds}
            onChange={(value) =>
              setPreferences({ ...preferences, defaultInboxUserIds: value })
            }
            options={admins.map((admin) => ({
              label: `${admin.firstName} ${admin.lastName}`,
              value: admin.id,
            }))}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />

          {admins.length === 0 && (
            <Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 8, display: "block" }}
            >
              No admins found for this organization.
            </Text>
          )}

          <Divider />

          <Title level={5}>Auto-Archive Settings</Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Automatically hide applications older than the specified number of
            days from overview views. Leave empty to show all applications
            regardless of age.
          </Text>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <Text strong>Auto-archive applications after</Text>
            <InputNumber
              min={1}
              max={365}
              style={{ width: 80 }}
              placeholder=""
              value={preferences.autoArchiveDays}
              onChange={(value) =>
                setPreferences({
                  ...preferences,
                  autoArchiveDays: value || undefined,
                })
              }
            />
            <Text strong>days</Text>
          </div>

          <Divider />

          <Title level={5}>Alert Thresholds</Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Configure when alerts should trigger warning or critical severity
            levels based on application counts.
          </Text>

          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {/* Pending Applications */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Pending Applications
              </Text>
              <Row gutter={16}>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Warning Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={
                      preferences.alertThresholds?.pendingApplications?.warning
                    }
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          pendingApplications: {
                            ...preferences.alertThresholds?.pendingApplications,
                            warning: value || 5,
                            critical:
                              preferences.alertThresholds?.pendingApplications
                                ?.critical || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Critical Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={
                      preferences.alertThresholds?.pendingApplications?.critical
                    }
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          pendingApplications: {
                            ...preferences.alertThresholds?.pendingApplications,
                            warning:
                              preferences.alertThresholds?.pendingApplications
                                ?.warning || 5,
                            critical: value || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
              </Row>
            </div>

            {/* Unread Applications */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Unread Applications
              </Text>
              <Row gutter={16}>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Warning Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={
                      preferences.alertThresholds?.unreadApplications?.warning
                    }
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          unreadApplications: {
                            ...preferences.alertThresholds?.unreadApplications,
                            warning: value || 5,
                            critical:
                              preferences.alertThresholds?.unreadApplications
                                ?.critical || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Critical Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={
                      preferences.alertThresholds?.unreadApplications?.critical
                    }
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          unreadApplications: {
                            ...preferences.alertThresholds?.unreadApplications,
                            warning:
                              preferences.alertThresholds?.unreadApplications
                                ?.warning || 5,
                            critical: value || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
              </Row>
            </div>

            {/* New Activity */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                New Activity
              </Text>
              <Row gutter={16}>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Warning Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={preferences.alertThresholds?.newActivity?.warning}
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          newActivity: {
                            ...preferences.alertThresholds?.newActivity,
                            warning: value || 5,
                            critical:
                              preferences.alertThresholds?.newActivity
                                ?.critical || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Critical Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={preferences.alertThresholds?.newActivity?.critical}
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          newActivity: {
                            ...preferences.alertThresholds?.newActivity,
                            warning:
                              preferences.alertThresholds?.newActivity
                                ?.warning || 5,
                            critical: value || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
              </Row>
            </div>

            {/* Unassigned Applications */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Unassigned Applications
              </Text>
              <Row gutter={16}>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Warning Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={preferences.alertThresholds?.unassigned?.warning}
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          unassigned: {
                            ...preferences.alertThresholds?.unassigned,
                            warning: value || 5,
                            critical:
                              preferences.alertThresholds?.unassigned
                                ?.critical || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
                <Col span={12}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                  >
                    Critical Threshold
                  </Text>
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    value={preferences.alertThresholds?.unassigned?.critical}
                    onChange={(value) =>
                      setPreferences({
                        ...preferences,
                        alertThresholds: {
                          ...preferences.alertThresholds,
                          unassigned: {
                            ...preferences.alertThresholds?.unassigned,
                            warning:
                              preferences.alertThresholds?.unassigned
                                ?.warning || 5,
                            critical: value || 10,
                          },
                        },
                      })
                    }
                  />
                </Col>
              </Row>
            </div>
          </Space>

          <Divider />

          <Title level={5} style={{ margin: 0, marginBottom: 16 }}>
            Status Categorization
          </Title>

          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Categorize decision statuses to improve metrics accuracy.
          </Text>

          {availableStatuses.length > 0 ? (
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {availableStatuses.map((status, index) => {
                // Check if we need to show product title to disambiguate
                const prevStatus =
                  index > 0 ? availableStatuses[index - 1] : null;
                const nextStatus =
                  index < availableStatuses.length - 1
                    ? availableStatuses[index + 1]
                    : null;
                const hasDuplicateTitle =
                  (prevStatus && prevStatus.title === status.title) ||
                  (nextStatus && nextStatus.title === status.title);

                // Determine badge styles based on decision - matching alert badge styles
                const getDecisionSeverity = (
                  decision: string | null
                ): BadgeSeverity | null => {
                  switch (decision) {
                    case "approved":
                      return "success";
                    case "denied":
                      return "critical";
                    case "review":
                      return "warning";
                    default:
                      return null;
                  }
                };

                const severity = status.decision
                  ? getDecisionSeverity(status.decision)
                  : null;

                return (
                  <div
                    key={status.uuid}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      border: `1px solid ${colors.gray}`,
                      borderRadius: "4px",
                      backgroundColor: colors.bodyBg,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Text
                          strong
                          style={{
                            color: status.deleted
                              ? "rgba(0,0,0,0.55)"
                              : undefined,
                          }}
                        >
                          {status.title}
                          {status.deleted && " (Disabled)"}
                        </Text>
                        {status.locked && (
                          <LockOutlined
                            style={{
                              color: colors.gray7,
                              fontSize: "12px",
                            }}
                            title="This status is locked"
                          />
                        )}
                        {status.decision && severity && (
                          <StatusBadge severity={severity}>
                            {status.decision}
                          </StatusBadge>
                        )}
                      </div>
                      {hasDuplicateTitle && (
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {status.financialInstitutionProduct?.title || ""}
                        </Text>
                      )}
                    </div>
                    <Select
                      style={{ width: 140 }}
                      placeholder="Categorize"
                      value={preferences.statusCategorization?.[status.uuid]}
                      onChange={(value) => {
                        setPreferences({
                          ...preferences,
                          statusCategorization: {
                            ...preferences.statusCategorization,
                            [status.uuid]: value,
                          },
                        });
                      }}
                      allowClear
                      options={[
                        { label: "Approved", value: "approved" },
                        { label: "Denied", value: "denied" },
                        { label: "Canceled", value: "canceled" },
                        { label: "Pending", value: "review" },
                      ]}
                    />
                  </div>
                );
              })}
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              No statuses found for this organization.
            </Text>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ApplicationsOverviewSettingsModal;
